/**
 * Batch Detail Page
 * Displays comprehensive details of a single inventory batch
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-001
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { inventoryBatchService } from '@/services/commerce/inventoryBatchService';
import type { BatchResponse } from '@/types/inventory';
import { QualityGradeConfig } from '@/types/inventory';
import { BatchStatusBadge } from '@/components/inventory/batches/BatchStatusBadge';
import { ProductTypeDisplay } from '@/components/inventory/batches/ProductTypeDisplay';
import { QuantityDisplay } from '@/components/inventory/batches/QuantityDisplay';
import { ExpiryWarning } from '@/components/inventory/batches/ExpiryWarning';
import { UpdateQualityDialog } from '@/components/inventory/batches/UpdateQualityDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Gauge,
  Package,
  Factory,
  User,
  Clock,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function BatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [batch, setBatch] = useState<BatchResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updateQualityDialogOpen, setUpdateQualityDialogOpen] = useState(false);

  useEffect(() => {
    if (!id) {
      navigate('/inventory/batches');
      return;
    }

    fetchBatchDetails();
  }, [id]);

  const fetchBatchDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await inventoryBatchService.getBatchById(parseInt(id!));
      setBatch(data);
    } catch (error: any) {
      console.error('Failed to fetch batch details:', error);
      setError(error.message || 'Failed to load batch details');
      toast.error('Failed to Load Batch', {
        description: error.message || 'Please try again later'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQualityUpdateSuccess = (updatedBatch: BatchResponse) => {
    setBatch(updatedBatch);
    toast.success('Quality Updated', {
      description: `Batch quality has been updated successfully`
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <Skeleton className="h-10 w-48 mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-64" />
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !batch) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/inventory/batches')}
            className="mb-8"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Batches
          </Button>
          <Alert className="border-red-300 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {error || 'Batch not found'}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const gradeConfig = QualityGradeConfig[batch.qualityGrade];
  const utilizationPercentage = ((batch.initialQuantity - batch.currentQuantity) / batch.initialQuantity) * 100;
  const availabilityPercentage = (batch.availableQuantity / batch.currentQuantity) * 100;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/inventory/batches')}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Batches
          </Button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{batch.batchNumber}</h1>
              <p className="text-gray-600 mt-2">
                Created on {format(new Date(batch.createdAt), 'PPP')}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setUpdateQualityDialogOpen(true)}
                className="flex items-center gap-2"
              >
                <Shield className="w-4 h-4" />
                Update Quality
              </Button>
              <BatchStatusBadge status={batch.batchStatus} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Expiry Warning */}
            <ExpiryWarning expiryDate={batch.expiryDate} />

            {/* Batch Information */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  <CardTitle>Batch Information</CardTitle>
                </div>
                <CardDescription>
                  Production and expiry details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Product Type */}
                  <div>
                    <div className="text-sm text-gray-500 mb-2">Product Type</div>
                    <ProductTypeDisplay productType={batch.productType} showDescription />
                  </div>

                  {/* Quality Grade */}
                  <div>
                    <div className="text-sm text-gray-500 mb-2">Quality Grade</div>
                    <Badge
                      className={cn(
                        'text-lg px-4 py-2',
                        gradeConfig.bgColor,
                        gradeConfig.color,
                        'border-none'
                      )}
                    >
                      Grade {batch.qualityGrade}
                    </Badge>
                    <div className="text-sm text-gray-600 mt-2">
                      {gradeConfig.description}
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Production Date */}
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-500">Production Date</div>
                      <div className="font-semibold text-gray-900">
                        {format(new Date(batch.productionDate), 'PPP')}
                      </div>
                    </div>
                  </div>

                  {/* Expiry Date */}
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-500">Expiry Date</div>
                      <div className="font-semibold text-gray-900">
                        {format(new Date(batch.expiryDate), 'PPP')}
                      </div>
                      {batch.daysUntilExpiry !== undefined && (
                        <div className="text-sm text-gray-600 mt-1">
                          {batch.daysUntilExpiry > 0
                            ? `${batch.daysUntilExpiry} days remaining`
                            : 'Expired'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quantity Tracking */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  <CardTitle>Quantity Tracking</CardTitle>
                </div>
                <CardDescription>
                  Current inventory levels and allocations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Quantity Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="pt-6">
                      <div className="text-sm text-blue-600 mb-1">Initial Quantity</div>
                      <QuantityDisplay
                        quantity={batch.initialQuantity}
                        unit={batch.quantityUnit}
                        variant="large"
                      />
                    </CardContent>
                  </Card>

                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-6">
                      <div className="text-sm text-green-600 mb-1">Available</div>
                      <QuantityDisplay
                        quantity={batch.availableQuantity}
                        unit={batch.quantityUnit}
                        variant="large"
                      />
                    </CardContent>
                  </Card>

                  <Card className="bg-orange-50 border-orange-200">
                    <CardContent className="pt-6">
                      <div className="text-sm text-orange-600 mb-1">Allocated</div>
                      <QuantityDisplay
                        quantity={batch.allocatedQuantity}
                        unit={batch.quantityUnit}
                        variant="large"
                      />
                    </CardContent>
                  </Card>
                </div>

                {/* Utilization */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-600">Utilization</span>
                    <span className="font-semibold text-gray-900">
                      {utilizationPercentage.toFixed(1)}% used
                    </span>
                  </div>
                  <Progress value={utilizationPercentage} className="h-2" />
                </div>

                {/* Availability */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-600">Availability</span>
                    <span className="font-semibold text-gray-900">
                      {availabilityPercentage.toFixed(1)}% available
                    </span>
                  </div>
                  <Progress value={availabilityPercentage} className="h-2" />
                </div>
              </CardContent>
            </Card>

            {/* Quality & Storage */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Factory className="h-5 w-5 text-purple-600" />
                  <CardTitle>Quality & Storage</CardTitle>
                </div>
                <CardDescription>
                  Quality metrics and storage details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Purity Percentage */}
                  <div>
                    <div className="text-sm text-gray-500 mb-2">Purity Percentage</div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-gray-900">
                        {batch.purityPercentage.toFixed(2)}
                      </span>
                      <span className="text-xl text-gray-600">%</span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">Methane content</div>
                  </div>

                  {/* Pressure */}
                  {batch.pressure && (
                    <div className="flex items-start gap-3">
                      <Gauge className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <div className="text-sm text-gray-500 mb-1">Pressure</div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold text-gray-900">
                            {batch.pressure.toFixed(2)}
                          </span>
                          <span className="text-sm text-gray-600">bar</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Storage Location */}
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Storage Location</div>
                    <div className="font-semibold text-gray-900">{batch.storageLocation}</div>
                  </div>
                </div>

                {/* PESO Compliant */}
                {batch.pesoCompliant && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800">
                      PESO Compliant
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Audit Information */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-gray-600" />
                  <CardTitle className="text-base">Audit Trail</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div>
                  <div className="text-gray-500 mb-1">Created By</div>
                  <div className="font-medium text-gray-900">{batch.createdBy}</div>
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Created At</div>
                  <div className="font-medium text-gray-900">
                    {format(new Date(batch.createdAt), 'PPp')}
                  </div>
                </div>
                {batch.updatedBy && (
                  <>
                    <Separator />
                    <div>
                      <div className="text-gray-500 mb-1">Last Updated By</div>
                      <div className="font-medium text-gray-900">{batch.updatedBy}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 mb-1">Last Updated At</div>
                      <div className="font-medium text-gray-900">
                        {format(new Date(batch.updatedAt!), 'PPp')}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-gray-600" />
                  <CardTitle className="text-base">Quick Stats</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Batch ID</span>
                  <span className="font-mono font-semibold text-gray-900">#{batch.id}</span>
                </div>
                {batch.utilizationPercentage !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Utilization</span>
                    <span className="font-semibold text-gray-900">
                      {batch.utilizationPercentage.toFixed(1)}%
                    </span>
                  </div>
                )}
                {batch.daysUntilExpiry !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Days to Expiry</span>
                    <span
                      className={cn(
                        'font-semibold',
                        batch.daysUntilExpiry < 0
                          ? 'text-red-600'
                          : batch.daysUntilExpiry <= 30
                          ? 'text-orange-600'
                          : 'text-green-600'
                      )}
                    >
                      {batch.daysUntilExpiry < 0
                        ? 'Expired'
                        : `${batch.daysUntilExpiry} days`}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Legacy Fields (if present) */}
            {(batch.externalId || batch.clusterId || batch.productionBatchId) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Legacy References</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {batch.externalId && (
                    <div>
                      <div className="text-gray-500 mb-1">External ID</div>
                      <div className="font-mono text-xs text-gray-900">{batch.externalId}</div>
                    </div>
                  )}
                  {batch.clusterId && (
                    <div>
                      <div className="text-gray-500 mb-1">Cluster ID</div>
                      <div className="font-mono text-xs text-gray-900">{batch.clusterId}</div>
                    </div>
                  )}
                  {batch.productionBatchId && (
                    <div>
                      <div className="text-gray-500 mb-1">Production Batch ID</div>
                      <div className="font-mono text-xs text-gray-900">
                        {batch.productionBatchId}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Update Quality Dialog */}
        <UpdateQualityDialog
          batch={batch}
          open={updateQualityDialogOpen}
          onOpenChange={setUpdateQualityDialogOpen}
          onSuccess={handleQualityUpdateSuccess}
        />
      </div>
    </div>
  );
}
